import React, {useEffect, useState} from "react";
import './constructor.styles.scss';
import {Box, Button, Dialog, LinearProgress} from "@mui/material";
import {useParams, useNavigate} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {CourseData} from "../../../../../types";
import {courseService} from "../../../../../services/course.service";
import Layout from "../../../../../shared/Layout";
import ConstructorStepOne from "./ConstructorStepOne/ConstructorStepOne";
import ConstructorStepTwo from "./ConstructorStepTwo/ConstructorStepTwo";
import ConstructorStepThree from "./ConstructorStepThree/ConstructorStepThree";
import ConstructorStepFour from "./ConstructorStepFour/ConstructorStepFour";
import ConstructorStepFive from "./ConstructorStepFive/ConstructorStepFive";
import CoursePreview from "../../../../Home/components/common/CoursePreview/CoursePreview";


export interface CourseGetCourses {
    category_id: string;
    id: string;
    name: string;
    price: number;
    rejected_comment: null | string;
    requesite_id: null | string;
    status: string;
}

export interface CourseValue {
    courseGoals: string[];
    courseRequirements: string[];
    courseAuditory: string[];
}


const useFetchGetCourses = () => {
    return useQuery<CourseGetCourses, Error>({
        queryKey: ['courses'],
        queryFn: () => courseService.getCourses().then(response => response.data),
    });
}


export const ConstructorCourse = () => {
    const {courseId} = useParams<{ courseId?: string }>();
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const {data: courseMe} = useFetchGetCourses();
    const [course , setCourse] = useState({
        category_id: '',
        id: '',
        name: '',
        price: 0,
        rejected_comment: null,
        requesite_id: null,
        status: '',

    })
    const { data: courseData, isLoading } = useQuery<CourseData, Error>({
        queryKey: ['courseData', course.id],
        queryFn: () => courseService.getCourseDetails(course.id)
    });

    const [step, setStep] = useState<number>(1);
    const [courseValue, setCourseValue] = useState<CourseValue>({
        courseGoals: [],
        courseRequirements: [],
        courseAuditory: [],
    });

    const navigate = useNavigate();

    useEffect(() => {
        if (courseMe && Array.isArray(courseMe)) {
            const foundCourse = courseMe.find(c => c.id === courseId);
            if (foundCourse) {
                setCourse(foundCourse);
            } else {
                console.log(`Курс с ID ${courseId} не найден.`);
            }
        }
    }, [courseMe, courseId]);
    useEffect(() => {
        const fetchCourseInfo = async () => {
            const id = courseId || '';
            try {
                const objectives = await courseService.getCourseObjectives(id);
                const requirements = await courseService.getCourseRequirements(id);
                const audience = await courseService.getCourseAuditory(id);
                const defaultGoals = objectives.data.length ? objectives.data : ["", "", "", ""];
                const defaultRequirements = requirements.data.length ? requirements.data : [""];
                const defaultAudience = audience.data.length ? audience.data : [""];
                setCourseValue({
                    courseGoals: defaultGoals,
                    courseRequirements: defaultRequirements,
                    courseAuditory: defaultAudience,
                });
            } catch (error) {
                console.error('Ошибка при получении информации о курсе:', error);
            }
        };

        fetchCourseInfo();
    }, [courseId]);

    const renderStepContent = () => {
        switch (step) {
            case 1:
                return <ConstructorStepOne
                    courseValue={courseValue}
                    onDataChange={handleStepChange}
                    saveDraft={saveDraft}
                    handleOpenPreview={handleOpenPreview}
                />;
            case 2:
                return <ConstructorStepTwo
                    course={courseData}
                    courseId={courseId}
                    setStep={setStep}
                    handleOpenPreview={handleOpenPreview}
                />;
            case 3:
                return <ConstructorStepThree
                    course={course}
                    setStep={setStep}
                    handleOpenPreview={handleOpenPreview}
                />;
            case 4:
                return <ConstructorStepFour
                    course={course}
                    handleStepChange={handleStepChange}
                    setStep={setStep}
                    handleOpenPreview={handleOpenPreview}
                />;
            case 5:
                return <ConstructorStepFive
                    course={course}
                    setStep={setStep}
                    handleOpenPreview={handleOpenPreview}
                />;
            default:
                return null;
        }
    };

    const handleStepChange = (stepData: any) => {
        setCourseValue(prevValue => ({
            ...prevValue,
            ...stepData
        }));
        if (step < 5) {
            setStep(step + 1);
        }
    }

    const saveDraft = async (courseValue: CourseValue, close?: boolean) => {
        const courseIdd = courseId || '';
        try {
            await courseService.setCourseObjectives(courseIdd, courseValue.courseGoals);
            await courseService.setCourseRequirements(courseIdd, courseValue.courseRequirements);
            await courseService.setCourseAuditory(courseIdd, courseValue.courseAuditory);
            if(close) navigate("/panel");
        } catch (error) {
            console.error('Failed to save draft', error);
        }
    }

    const handleOpenPreview = () => {
        setIsPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
    };


    if (isLoading) {
        return (
            <Box>
                <LinearProgress/>
            </Box>
        )
    }

    if (!courseData) {
        return <Layout><Box>Курс не найден</Box></Layout>;
    }
    return (
        <Box>
            <Dialog fullScreen open={isPreviewOpen} onClose={handleClosePreview}>
                <CoursePreview
                    course={course}
                    onClose={handleClosePreview}
                />
            </Dialog>
            <Box>
                <div className="course-create-header">
                    <div className="course-create-header-info">
                        <h4>Программа курса | {course.name}</h4>
                        <div className="steps-count">Шаг {step} из 5</div>
                    </div>

                    <Button className="close-modal-btn"
                            variant="outlined"
                            href={`/panel`}
                            sx={{
                                marginLeft: 'auto',
                            }}
                    >
                        Выйти
                    </Button>
                </div>
                <div className="course-create-body">
                    <div className="step-indicator" style={{padding: '24px 108px 0 108px'}}>
                        <div className={step >= 1 ? "step-indicator-item active" : "step-indicator-item"}></div>
                        <div className={step >= 2 ? "step-indicator-item active" : "step-indicator-item"}></div>
                        <div className={step >= 3 ? "step-indicator-item active" : "step-indicator-item"}></div>
                        <div className={step >= 4 ? "step-indicator-item active" : "step-indicator-item"}></div>
                        <div className={step >= 5 ? "step-indicator-item active" : "step-indicator-item"}></div>
                    </div>
                    <div className="course-create-body">
                        {renderStepContent()}
                    </div>
                </div>
            </Box>
        </Box>
    );
}
